import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BACKEND_URL, formatApiError, tokenStore } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Check, ExternalLink, Film, Image as ImageIcon, Instagram, Loader2, ShieldOff, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Til gjennomgang" },
  { key: "approved", label: "Publisert" },
  { key: "rejected", label: "Avvist" },
  { key: "videos", label: "Videoer" },
];

export default function CustomerPhotosAdmin() {
  const [tab, setTab] = useState("pending");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // photo currently viewed in modal
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [params] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "videos") {
        const { data } = await api.get("/admin/customer-videos");
        setPhotos(data || []);
      } else {
        const { data } = await api.get("/admin/customer-photos", { params: { status: tab } });
        setPhotos(data || []);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [tab]);

  // Deep-link support: /admin/customer-photos?focus=<id>
  useEffect(() => {
    const focusId = params.get("focus");
    if (!focusId || photos.length === 0) return;
    const found = photos.find((p) => p.id === focusId);
    if (found) setSelected(found);
  }, [params, photos]);

  const filtered = useMemo(() => {
    if (!search.trim()) return photos;
    const q = search.trim().toLowerCase();
    return photos.filter((p) =>
      [p.user_name, p.boat_name, p.caption]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [photos, search]);

  const raw = (id) =>
    `${BACKEND_URL}/api/admin/customer-photo/${id}/raw?auth=${encodeURIComponent(tokenStore.get())}`;
  const publicUrl = (id) => `${BACKEND_URL}/api/public/customer-photo/${id}`;

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/customer-photos/${id}/approve`);
      toast.success("Godkjent – vises på Kontor-TV.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  const openReject = (p) => {
    setSelected(p);
    setRejectReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!selected) return;
    setBusyId(selected.id);
    try {
      await api.post(`/admin/customer-photos/${selected.id}/reject`, {
        review_notes: rejectReason,
      });
      toast.success("Bildet er avvist.");
      setRejectOpen(false);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slett bildet permanent?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/customer-photos/${id}`);
      toast.success("Bildet er slettet.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  const copyPublicUrl = async (p) => {
    try {
      await navigator.clipboard.writeText(publicUrl(p.id));
      toast.success("Offentlig URL kopiert.");
    } catch {
      toast.error("Kunne ikke kopiere til utklippstavlen.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="customer-photos-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39] font-semibold">
            Sosial deling
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824]">
            Kundebilder
          </h1>
          <p className="text-sm text-[#2B3F4D] mt-1 max-w-xl">
            Turbilder fra kundene. Godkjenn de fineste for Kontor-TV og
            Instagram. Kunder ser status &laquo;Live på kontoret&raquo; så snart du
            godkjenner.
          </p>
        </div>
        <div className="w-full sm:w-80">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk kunde, båt, eller tekst…"
            data-testid="customer-photos-search"
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`customer-photos-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
              tab === t.key
                ? "bg-[#0C1824] text-white border-[#0C1824]"
                : "bg-white text-[#0C1824] border-[#0C1824]/20 hover:border-[#BD984D]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Card className="p-10 flex items-center justify-center text-[#A57F39]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Laster {tab === "videos" ? "videoer" : "bilder"}…
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <ImageIcon className="w-10 h-10 mx-auto text-[#BD984D] mb-3" />
          <div className="font-semibold text-[#0C1824]">
            {tab === "videos"
              ? "Ingen videoer ennå."
              : "Ingen bilder i denne kategorien."}
          </div>
          <div className="text-sm text-[#2B3F4D] mt-1">
            {tab === "videos"
              ? "Kundevideoer havner her – og i Google Drive-mappen «Kundevideoer»."
              : "Nye kundebilder havner her så snart de blir sendt inn."}
          </div>
        </Card>
      ) : tab === "videos" ? (
        <VideoList
          videos={filtered}
          onDelete={async (id) => {
            if (!window.confirm("Slett videoen permanent?")) return;
            setBusyId(id);
            try {
              await api.delete(`/admin/customer-videos/${id}`);
              toast.success("Videoen er slettet.");
              load();
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || e.message);
            } finally {
              setBusyId("");
            }
          }}
          busyId={busyId}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="customer-photos-grid">
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`customer-photo-card-${p.id}`}
              className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition cursor-pointer"
              onClick={() => setSelected(p)}
            >
              <div className="aspect-square bg-slate-100">
                <img
                  src={raw(p.id)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-xs uppercase tracking-widest text-[#A57F39] font-semibold truncate">
                  {p.boat_name || "Ukjent båt"}
                </div>
                <div className="text-sm font-semibold text-[#0C1824] truncate">
                  {p.user_name || "Ukjent kunde"}
                </div>
                {p.caption && (
                  <div className="text-xs text-[#2B3F4D] mt-1 line-clamp-2">
                    {p.caption}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-2">
                  {p.allow_social && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-pink-700 bg-pink-100 rounded px-1.5 py-0.5">
                      <Instagram className="w-3 h-3" /> Instagram OK
                    </span>
                  )}
                  {!p.allow_social && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                      <ShieldOff className="w-3 h-3" /> Kun TV
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected && !rejectOpen} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#0C1824]">
                  <ImageIcon className="w-5 h-5 text-[#BD984D]" />
                  {selected.user_name || "Ukjent kunde"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-lg overflow-hidden bg-slate-100">
                  <img
                    src={raw(selected.id)}
                    alt=""
                    className="w-full max-h-[70vh] object-contain"
                    data-testid="customer-photo-modal-image"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
                      Båt
                    </div>
                    <div className="text-sm text-[#0C1824]">
                      {selected.boat_name || "Ukjent båt"}
                    </div>
                  </div>
                  {selected.caption && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
                        Kundens tekst
                      </div>
                      <div className="text-sm text-[#2B3F4D] whitespace-pre-wrap">
                        {selected.caption}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
                      Samtykke
                    </div>
                    <div className="text-sm text-[#0C1824]">
                      {selected.allow_social
                        ? "Kunden godtar at bildet brukes på Instagram / markedsføring."
                        : "Kunden ønsker at bildet KUN vises på kontor-TV, ikke i sosiale medier."}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
                      Lastet opp
                    </div>
                    <div className="text-sm text-[#2B3F4D]">
                      {selected.created_at ? new Date(selected.created_at).toLocaleString("nb-NO") : "—"}
                    </div>
                  </div>
                  {selected.status === "rejected" && selected.review_notes && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">
                        Avvisningsnotat
                      </div>
                      <div className="text-sm text-red-800 whitespace-pre-wrap">
                        {selected.review_notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-3 flex-wrap gap-2">
                {selected.status !== "approved" && (
                  <Button
                    onClick={() => approve(selected.id)}
                    disabled={busyId === selected.id}
                    data-testid="customer-photo-approve"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Godkjenn
                  </Button>
                )}
                {selected.status !== "rejected" && (
                  <Button
                    onClick={() => openReject(selected)}
                    disabled={busyId === selected.id}
                    variant="outline"
                    data-testid="customer-photo-reject"
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Avvis
                  </Button>
                )}
                {selected.status === "approved" && (
                  <Button
                    onClick={() => copyPublicUrl(selected)}
                    variant="outline"
                    data-testid="customer-photo-copy-url"
                    className="border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/5"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    Kopier offentlig URL
                  </Button>
                )}
                <Button
                  onClick={() => remove(selected.id)}
                  disabled={busyId === selected.id}
                  variant="outline"
                  data-testid="customer-photo-delete"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Slett
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason modal */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avvis bilde</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-[#2B3F4D]">
              Gi kunden en kort forklaring. Denne teksten vises i kundeportalen.
            </p>
            <Textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="F.eks. bildet er uklart, vi trenger et annet vinkel, …"
              data-testid="customer-photo-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={confirmReject}
              disabled={!rejectReason.trim() || busyId === selected?.id}
              data-testid="customer-photo-reject-confirm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Avvis bilde
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideoList({ videos, onDelete, busyId }) {
  const fmtDur = (s) => {
    if (!s) return "—";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const fmtMB = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;
  const raw = (id) =>
    `${BACKEND_URL}/api/customer/videos/${id}/raw?auth=${encodeURIComponent(tokenStore.get())}`;

  return (
    <div className="space-y-3" data-testid="customer-videos-list">
      <div className="rounded-lg bg-[#BD984D]/10 border border-[#BD984D]/30 p-4 text-sm text-[#0C1824] leading-relaxed">
        <div className="font-semibold flex items-center gap-1.5 mb-1">
          <Film className="w-4 h-4" /> Videoene vises ikke på Kontor-TV
        </div>
        Alle klipp havner i Google Drive-mappen{" "}
        <span className="font-mono text-xs bg-white/60 rounded px-1.5 py-0.5">Kundevideoer/</span>{" "}
        for enkel henting og videre bruk.
      </div>
      {videos.map((v) => (
        <div
          key={v.id}
          data-testid={`customer-video-card-${v.id}`}
          className="grid md:grid-cols-[280px_1fr_auto] gap-4 rounded-xl border border-slate-200 bg-white p-4 items-center"
        >
          <video
            src={raw(v.id)}
            controls
            preload="metadata"
            playsInline
            className="w-full rounded-lg bg-black max-h-52"
          />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#A57F39] font-semibold truncate">
              {v.boat_name || "Ukjent båt"}
            </div>
            <div className="text-base font-semibold text-[#0C1824] truncate">
              {v.user_name || "Ukjent kunde"}
            </div>
            {v.caption && (
              <div className="text-sm text-[#2B3F4D] mt-1 line-clamp-3">
                {v.caption}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-[#2B3F4D]">
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
                {fmtDur(v.duration)}
              </span>
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
                {fmtMB(v.size)}
              </span>
              {v.allow_social ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-pink-700 bg-pink-100 rounded px-1.5 py-0.5">
                  <Instagram className="w-3 h-3" /> Instagram OK
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                  <ShieldOff className="w-3 h-3" /> Kun arkiv
                </span>
              )}
              <span className="text-slate-500">
                · {new Date(v.created_at).toLocaleString("nb-NO", {
                  year: "numeric", month: "2-digit", day: "2-digit",
                  hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {v.drive_view_url && (
              <a
                href={v.drive_view_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`customer-video-drive-${v.id}`}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/5 px-3 py-2 text-sm font-semibold"
              >
                <ExternalLink className="w-4 h-4" />
                Åpne i Drive
              </a>
            )}
            <Button
              onClick={() => onDelete(v.id)}
              disabled={busyId === v.id}
              variant="outline"
              data-testid={`customer-video-delete-${v.id}`}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
